import { useContext } from 'react';

import { LocationSessionContext } from '../context';
import type { LocationSessionContextValue } from '../types';

/**
 * Outside a LocationSessionProvider (any render tree that isn't under
 * App.tsx's GuardedLayout — most component/hook unit tests included), the
 * safe default is "definitely not a location viewer": the same no-op
 * passthrough the provider itself guarantees for every non-location viewer
 * kind, not a thrown error. A location-only method (select/relock/etc.)
 * called in that state still throws — that IS a genuine bug — but merely
 * reading `isLocationViewer`/`session` must never force every unrelated
 * component test in the app to wrap itself in this provider.
 */
const NOT_A_LOCATION_VIEWER: LocationSessionContextValue = {
  isLocationViewer: false,
  isLoading: false,
  locationCode: null,
  session: null,
  lastTeacher: null,
  select: async () => {
    throw new Error('useLocationSession: select() called outside a LocationSessionProvider');
  },
  relock: async () => {
    throw new Error('useLocationSession: relock() called outside a LocationSessionProvider');
  },
  reset: async () => {
    throw new Error('useLocationSession: reset() called outside a LocationSessionProvider');
  },
  switchOpen: false,
  openSwitch: () => {
    throw new Error('useLocationSession: openSwitch() called outside a LocationSessionProvider');
  },
  closeSwitch: () => {
    throw new Error('useLocationSession: closeSwitch() called outside a LocationSessionProvider');
  },
  viewingGroupName: null,
  // Chip-label hint, not a location-only mutation — no-op, never throw.
  setViewingGroupName: () => {},
};

export function useLocationSession(): LocationSessionContextValue {
  return useContext(LocationSessionContext) ?? NOT_A_LOCATION_VIEWER;
}
